import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Upload, Check, AlertTriangle, Zap, Search, CloudOff, Eye, RotateCcw } from 'lucide-react';
import {
  useDailyRoster,
  useValidationStatus,
  useValidateDay,
  useValidateDayFinal,
  useReopenDay,
  useDecisionTypes,
  useEntities,
  useMe,
  useSettings,
  useHolidays,
  useAttendanceCoverage,
  type DailyRosterEntry,
  type AttendanceCoverageDay,
} from '../api/hooks.js';
import { useUiStore } from '../store/ui.js';
import { todayYmd, fmtFrLong, addDaysToYmd } from '../lib/date.js';
import { isPendingRow, pendingSummary } from '../lib/saisie.js';
import { DateNavigator } from '../components/DateNavigator.js';
import { DailyEntryTable } from '../components/DailyEntryTable.js';
import { ArbitrageView } from '../components/ArbitrageView.js';
import { DayValidated } from '../components/DayValidated.js';
import { ImportPointeuseModal } from '../components/ImportPointeuseModal.js';
import { WorkflowIndicator } from '../components/WorkflowIndicator.js';
import { toast } from '../store/toast.js';
import { cgSigned } from '../components/conges/common.js';
import '../styles/saisie-quotidienne.css';

function countTag(rows: DailyRosterEntry[], type: string): number {
  return rows.filter((r) => r.tags.some((t) => t.type === type)).length;
}

// ─── Day-chip tooltip ────────────────────────────────────────
// The chip encodes its state in a border colour; the tooltip spells that
// state out and backs it with the coverage counts, so a green chip says
// how many lines were validated and a pale one says how much is waiting.
function dayChipTitle(ymd: string, c: AttendanceCoverageDay | undefined): string {
  const day = fmtFrLong(ymd);
  const punches = c?.punches ?? 0;
  const validatedRecords = c?.validatedRecords ?? 0;
  const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n > 1 ? many : one}`;
  if (c?.validated) {
    return `${day}\nJournée validée · ${plural(validatedRecords, 'ligne validée', 'lignes validées')}\n${plural(punches, 'pointage importé', 'pointages importés')}`;
  }
  if (c?.prepared) {
    return `${day}\nEn revue · saisie validée, validation finale attendue\n${plural(punches, 'pointage importé', 'pointages importés')}`;
  }
  if (punches > 0) {
    return `${day}\nÀ valider · ${plural(punches, 'pointage importé', 'pointages importés')}\nAucune ligne validée pour l'instant`;
  }
  return `${day}\nPointeuse non importée · aucun pointage pour ce jour`;
}

// ─── Week-end / férié fun state ──────────────────────────────
// A non-working day has nothing to point — instead of an empty roster,
// the page grins. The joke is picked deterministically from the date so
// each day keeps its own line (and tests stay stable).
const WEEKEND_JOKES = [
  'La pointeuse aussi fait la grasse matinée.',
  'Zéro retard, zéro absence : journée parfaite.',
  'Même la machine à café a badgé sa sortie.',
  'Seul pointage du jour : le couscous de 13h.',
  'Aucun badge à l’horizon — que des transats.',
  'Repos réglementaire, ordre du calendrier.',
  'Les badges se rechargent. Vous aussi.',
];
const FERIE_JOKES = [
  'Férié officiel : la pointeuse chôme, vous aussi.',
  'Jour férié — le seul retard toléré est celui du réveil.',
  'La pointeuse a posé sa journée, comme tout le monde.',
];
const FLOATERS = ['☕', '😴', '🍉', '⛱️', '🎈'];
function pickJoke(dateStr: string, jokes: readonly string[]): string {
  let h = 0;
  for (const c of dateStr) h = (h * 31 + c.charCodeAt(0)) | 0;
  return jokes[Math.abs(h) % jokes.length]!;
}

function OffDayHero({ date, ferieName, lastWorking, nextWorking, onGo }: {
  date: string;
  ferieName: string | null;
  lastWorking: string;
  nextWorking: string;
  onGo: (d: string) => void;
}) {
  const ferie = ferieName != null;
  const joke = pickJoke(date, ferie ? FERIE_JOKES : WEEKEND_JOKES);
  return (
    <div className={'sq-offday' + (ferie ? ' is-ferie' : '')} data-testid="sq-offday">
      <span className="sq-offday-blob a" aria-hidden="true" />
      <span className="sq-offday-blob b" aria-hidden="true" />
      {FLOATERS.map((f, i) => (
        <span key={i} className={`sq-offday-float f${i + 1}`} aria-hidden="true">{f}</span>
      ))}
      <span className="sq-offday-emoji" aria-hidden="true">{ferie ? '🎉' : '🏖️'}</span>
      <h2 className="sq-offday-title">
        {ferie ? <>Jour férié — {ferieName}</> : 'C’est le week-end !'}
      </h2>
      <p className="sq-offday-joke">{joke}</p>
      <p className="sq-offday-sub">
        Jour non ouvré : aucun pointage attendu, aucune exception à arbitrer.
        {!ferie && ' La pointeuse rouvre dimanche.'}
      </p>
      <div className="sq-offday-actions">
        <button type="button" className="btn btn-sm btn-primary" onClick={() => onGo(lastWorking)} data-testid="sq-offday-prev">
          ← Dernier jour ouvré
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => onGo(nextWorking)} data-testid="sq-offday-next">
          Prochain jour ouvré →
        </button>
      </div>
    </div>
  );
}

export function DailyEntryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get('date') ?? todayYmd();
  const entityId = useUiStore((s) => s.currentEntityId);
  const roster = useDailyRoster({ date, entityId });
  const validation = useValidationStatus({ date, entityId });
  const validateDay = useValidateDay();
  const validateFinal = useValidateDayFinal();
  const reopenDay = useReopenDay();
  const me = useMe();
  // Only the second signature may close a day, edit one under review, or
  // reopen a closed one — the whole point of splitting the two.
  const canValidateFinal = me.data?.permissions?.includes('attendance.validate_final') ?? false;
  const { data: decisionTypes } = useDecisionTypes();
  const { data: entities } = useEntities();
  // All-entities roster (counts per entity for the header EntityBar),
  // independent of the active filter.
  const allRoster = useDailyRoster({ date, entityId: 'all' });
  const setEntityBarExtras = useUiStore((s) => s.setEntityBarExtras);
  const [importOpen, setImportOpen] = useState(false);

  // Working-day predicate for the navigator: weekend (per workDays) or
  // a holiday. Holidays are fetched for the viewed date's year.
  const settings = useSettings();
  const navYear = Number(date.slice(0, 4));
  const holidays = useHolidays(`${navYear}-01-01`, `${navYear}-12-31`);
  const workDays = useMemo(() => {
    const raw = settings.data?.workDays;
    return Array.isArray(raw) ? (raw as number[]) : [0, 1, 2, 3, 4];
  }, [settings.data]);
  const holidaySet = useMemo(
    () => new Set((holidays.data ?? []).map((h) => h.date)),
    [holidays.data],
  );
  const isNonWorking = useMemo(
    () => (ymd: string) => {
      const dow = new Date(`${ymd}T00:00:00Z`).getUTCDay();
      return !workDays.includes(dow) || holidaySet.has(ymd);
    },
    [workDays, holidaySet],
  );

  // Data coverage of the trailing 2 weeks: which days actually have
  // imported pointeuse data. Grays out empty days in the day strip and
  // swaps the roster for a "rien d'importé" state to avoid reading a
  // non-imported day as everyone-absent.
  const coverFrom = addDaysToYmd(date, -13);
  const coverage = useAttendanceCoverage({ from: coverFrom, to: date, entityId });
  const coverByDate = useMemo(
    () => new Map((coverage.data?.days ?? []).map((d) => [d.date, d])),
    [coverage.data],
  );
  const stripDays = useMemo(() => {
    const out: string[] = [];
    for (let d = coverFrom; d <= date; d = addDaysToYmd(d, 1)) {
      if (!isNonWorking(d)) out.push(d);
    }
    return out;
  }, [coverFrom, date, isNonWorking]);
  const selCover = coverByDate.get(date);
  const notImported =
    !coverage.isLoading &&
    !isNonWorking(date) &&
    (selCover?.punches ?? 0) === 0 &&
    !(selCover?.validated ?? false);
  const [showAnyway, setShowAnyway] = useState(false);

  // Week-end / férié: nothing to point — the roster gives way to the
  // off-day hero. Waits for settings so the state can't flash while the
  // workDays config is still loading.
  const offDay = settings.data != null && isNonWorking(date);
  const ferieName = holidaySet.has(date)
    ? (holidays.data ?? []).find((h) => h.date === date)?.name ?? 'férié'
    : null;
  const walkWorking = (from: string, step: number): string => {
    let d = from;
    for (let i = 0; i < 31 && isNonWorking(d); i++) d = addDaysToYmd(d, step);
    return d;
  };
  const lastWorking = walkWorking(addDaysToYmd(date, -1), -1);
  const nextWorking = walkWorking(addDaysToYmd(date, 1), 1);

  const STRIP_FMT = useMemo(
    () => new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', timeZone: 'UTC' }),
    [],
  );

  // View state (matches the prototype's single-state model).
  const [mode, setMode] = useState<'ledger' | 'arbitrage'>('ledger');
  const [arbStart, setArbStart] = useState<string | undefined>(undefined);
  const [reviewing, setReviewing] = useState(false); // "Revoir le tableau" after validation
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl+K focuses the employee search (proto §10).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Feed per-entity day counts + caption into the shared header EntityBar
  // (Saisie-only enrichment); clear on unmount so other screens are plain.
  useEffect(() => {
    const counts: Record<string, number> = {};
    for (const r of allRoster.data ?? []) {
      counts[r.employee.entityId] = (counts[r.employee.entityId] ?? 0) + 1;
    }
    const list = entities ?? [];
    const name = entityId === 'all' ? 'Toutes' : list.find((e) => e.id === entityId)?.short ?? entityId;
    setEntityBarExtras({ counts, caption: `${list.length} entité${list.length > 1 ? 's' : ''} · vue ${name}` });
    return () => setEntityBarExtras(null);
  }, [allRoster.data, entities, entityId, setEntityBarExtras]);

  // Changing day leaves the arbitrage flow (its queue is frozen on the
  // day it opened) but KEEPS the ledger/summary preference: someone who
  // asked to see the table keeps seeing tables as they walk the week.
  const setDate = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('date', next);
    setSearchParams(params, { replace: true });
    setMode('ledger');
    setShowAnyway(false);
  };

  const importDisabled = entityId === 'all';
  const importDisabledHint = 'Sélectionnez une entité pour importer.';

  const rows = roster.data ?? [];
  const stage = validation.data?.stage ?? 'DRAFT';
  const blockers = validation.data?.blockers ?? [];
  const missingDecisions = blockers.filter((b) => b.missing.includes('decision')).length;
  const missingNotified = blockers.filter((b) => b.missing.includes('notified')).length;
  const pendingRows = rows.filter(isPendingRow);
  const ready = entityId !== 'all' && blockers.length === 0 && rows.length > 0;
  const validated = stage === 'VALIDATED';
  // Signed once, waiting for the second signature. The table stays open —
  // that is the whole point of the review step — but only a reviewer can
  // write to it (the server enforces it; the UI just says so).
  const inReview = stage === 'VALIDATED_L1';
  // The server refuses these writes; the table stops offering them rather
  // than letting someone type into a field that will bounce.
  const dayReadOnly = validated || (inReview && !canValidateFinal);
  const dayReadOnlyHint = validated
    ? 'Journée validée — rouvrez-la pour la modifier.'
    : 'Journée en revue — seule la validation finale peut encore la modifier.';

  // Qui a réellement travaillé cette journée, pour l'indicateur de
  // workflow — le plus fréquent l'emporte quand plusieurs personnes ont
  // saisi. Aucun nom inventé: une étape sans acteur connu reste anonyme.
  const workflowActors = useMemo(() => {
    const tally = new Map<string, number>();
    for (const r of rows) {
      const n = r.record?.decisionSetByName;
      if (n) tally.set(n, (tally.get(n) ?? 0) + 1);
    }
    // Whoever signed the saisie owns step 1; before that signature, the
    // most frequent decision author stands in.
    const signedL1 = rows.find((r) => r.record?.validatedL1ByName)?.record?.validatedL1ByName ?? null;
    const saisie = signedL1 ?? [...tally.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
    const validation = rows.find((r) => r.record?.validatedByName)?.record?.validatedByName ?? null;
    // The reviewer and the closer are the same person; naming them on the
    // Revue step only once the day is closed avoids guessing who will act.
    return { saisie, revue: validation, validation };
  }, [rows]);

  // Search filters only the displayed rows; banner + validation counts
  // stay anchored to the full day's roster.
  const q = query.trim().toLowerCase();
  const visibleRows = q
    ? rows.filter(
        (r) =>
          r.employee.name.toLowerCase().includes(q) ||
          r.employee.code.toLowerCase().includes(q),
      )
    : rows;
  const summary = pendingSummary(rows);

  const customLabel = (code: string) => decisionTypes?.find((d) => d.code === code)?.label;

  const launch = (employeeId?: string) => {
    setArbStart(employeeId);
    setMode('arbitrage');
  };

  const workflowFailure = (err: unknown, fallback: string) => {
    const e = err as Error & { body?: { error?: string; message?: string } };
    const known: Record<string, string> = {
      validation_incomplete: 'Validation impossible : des champs requis manquent.',
      already_prepared: 'La saisie est déjà validée — la journée attend sa validation finale.',
      not_prepared: 'La saisie doit être validée avant la validation finale.',
      day_locked: 'Journée déjà validée — rouvrez-la pour la reprendre.',
      not_validated: "Cette journée n'est pas validée — il n'y a rien à rouvrir.",
    };
    toast.danger(known[e.body?.error ?? ''] ?? e.body?.message ?? fallback);
  };

  // Première signature — la saisie est finie, la journée part en revue.
  const onValidate = async () => {
    try {
      const res = await validateDay.mutateAsync({ entityId, date });
      toast.success(
        `Saisie validée · ${res.prepared} ligne${res.prepared > 1 ? 's' : ''} · en attente de la validation finale.`,
      );
    } catch (err) {
      workflowFailure(err, 'Échec de la validation.');
    }
  };

  // Seconde signature — la revue clôture la journée et débite les soldes.
  // Le récapitulatif part une fois toutes les sociétés du jour validées.
  const onValidateFinal = async () => {
    try {
      const res = await validateFinal.mutateAsync({ entityId, date });
      toast.success(`Journée validée · ${res.validated} lignes · le récapitulatif partira quand toutes les sociétés seront validées.`);
      // Un solde sous zéro ne bloque plus la validation — mais il faut le
      // voir tout de suite, avec le nom, sinon personne ne sait quoi créditer.
      const neg = res.negativeBalances ?? [];
      if (neg.length > 0) {
        const who = neg
          .map((n) => `${n.name} (${n.type === 'RECUP' ? 'récup' : 'congé'} ${cgSigned(n.balanceAfter)} j)`)
          .join(', ');
        toast.danger(`Solde négatif après débit : ${who} — à créditer dans Congés.`);
      }
    } catch (err) {
      workflowFailure(err, 'Échec de la validation finale.');
    }
  };

  const onReopen = async () => {
    try {
      const res = await reopenDay.mutateAsync({ entityId, date });
      setReviewing(true);
      toast.success(
        res.reversedLeaveDebits > 0
          ? `Journée rouverte · ${res.reversedLeaveDebits} débit${res.reversedLeaveDebits > 1 ? 's' : ''} de congé contre-passé${res.reversedLeaveDebits > 1 ? 's' : ''}.`
          : 'Journée rouverte — elle repasse en attente de validation finale.',
      );
    } catch (err) {
      workflowFailure(err, 'Échec de la réouverture.');
    }
  };

  // ─── Persistent chrome ─────────────────────────────────────
  // The date navigator and the day strip sit ABOVE the view switch, not
  // inside one of its states. A validated day used to open on a summary
  // screen with no arrows at all: the only way out was « Revoir le
  // tableau », and the next day — if also validated — trapped you again.
  // Navigation belongs to the page, whatever the day's state.
  //
  // `withTools` = the ledger's own controls (search filters the table,
  // import targets the open day). The arbitrage flow and the validated
  // summary get the bare navigator: neither has a table to filter.
  const renderDateBar = (withTools: boolean) => (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
      <DateNavigator value={date} onChange={setDate} isNonWorking={isNonWorking} />
      <div style={{ flex: 1 }} />
      {withTools && !offDay && (
        <>
          <div className="sq-search">
            <Search size={14} aria-hidden="true" className="sq-search-icon" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un employé…"
              aria-label="Rechercher un employé"
              data-testid="employee-search"
            />
            <kbd className="sq-search-kbd">⌘K</kbd>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setImportOpen(true)}
            disabled={importDisabled}
            title={importDisabled ? importDisabledHint : undefined}
            aria-label={importDisabled ? importDisabledHint : 'Importer le pointage'}
            data-testid="open-import-modal"
          >
            <Upload aria-hidden="true" /> Importer pointeuse
          </button>
        </>
      )}
    </div>
  );

  const dayStrip = (
    <>
      {/* Day strip — trailing working days, with a 3-state key. The state
          is the whole point of the strip, so it gets both a visible legend
          (the only form that survives on touch, where nothing hovers) and a
          counted tooltip built from the coverage numbers. */}
      <div className="sq-daystrip" data-testid="sq-daystrip" role="group" aria-label="Jours récents">
        {stripDays.map((d) => {
          const c = coverByDate.get(d);
          const isValidated = c?.validated ?? false;
          const isPrepared = !isValidated && (c?.prepared ?? false);
          const punches = c?.punches ?? 0;
          const hasData = punches > 0 || isValidated || isPrepared;
          const cls =
            'sq-daychip' +
            (d === date ? ' active' : '') +
            (hasData ? '' : ' empty') +
            (isValidated ? ' validated' : isPrepared ? ' inreview' : ' pending');
          const label = STRIP_FMT.format(new Date(`${d}T00:00:00Z`));
          return (
            <button
              key={d}
              type="button"
              className={cls}
              onClick={() => setDate(d)}
              title={dayChipTitle(d, c)}
              data-testid={`sq-daychip-${d}`}
            >
              <span className="sq-daychip-lbl">{label}</span>
              {isValidated ? <Check size={10} aria-hidden="true" className="sq-daychip-check" /> : null}
            </button>
          );
        })}
      </div>
      <div className="sq-daystrip-legend" aria-hidden="true">
        <span className="sq-legend-item">
          <span className="sq-legend-chip validated" />
          journée validée
        </span>
        <span className="sq-legend-item">
          <span className="sq-legend-chip inreview" />
          en revue
        </span>
        <span className="sq-legend-item">
          <span className="sq-legend-chip pending" />
          importée, à valider
        </span>
        <span className="sq-legend-item">
          <span className="sq-legend-chip empty" />
          pointeuse non importée
        </span>
      </div>
    </>
  );

  // ── Arbitrage view ─────────────────────────────────────────
  if (mode === 'arbitrage') {
    return (
      <div className="sq-root">
        <div className="page-header">
          <div className="page-eyebrow">Saisie · {fmtFrLong(date)}</div>
          <h1 className="page-title">Arbitrage des exceptions</h1>
        </div>
        {renderDateBar(false)}
        <ArbitrageView
          date={date}
          rows={rows}
          startEmployeeId={arbStart}
          onExit={() => setMode('ledger')}
        />
      </div>
    );
  }

  // ── Validated view ─────────────────────────────────────────
  if (validated && !reviewing) {
    return (
      <div className="sq-view sq-root">
        {renderDateBar(false)}
        {dayStrip}
        <DayValidated
          date={date}
          entityId={entityId}
          rows={rows}
          validatedCount={rows.length}
          validatedBy={validation.data?.validatedBy ?? null}
          onBack={() => setReviewing(true)}
          onReopen={canValidateFinal ? onReopen : undefined}
          reopening={reopenDay.isPending}
          customLabel={customLabel}
        />
      </div>
    );
  }

  // ── Ledger view ────────────────────────────────────────────
  return (
    <div className="sq-view sq-root">
      <div className="page-header">
        <div className="page-eyebrow">Saisie · {fmtFrLong(date)}</div>
        <h1 className="page-title">Pointage du jour</h1>
        <p className="page-subtitle">
          {offDay
            ? 'Jour non ouvré — rien à pointer.'
            : `${rows.length} enregistrement${rows.length > 1 ? 's' : ''} pour ce filtre.`}
        </p>
      </div>

      {renderDateBar(true)}

      {dayStrip}

      {offDay && (
        <OffDayHero
          date={date}
          ferieName={ferieName}
          lastWorking={lastWorking}
          nextWorking={nextWorking}
          onGo={setDate}
        />
      )}

      {!offDay && <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
        <WorkflowIndicator stage={stage} actors={workflowActors} />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--ink-3)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', animation: 'softPulse 2s infinite' }}
            aria-hidden="true"
          />
          {validated
            ? 'Journée validée'
            : inReview
              ? 'En revue · validation finale attendue'
              : 'Brouillon ouvert · sauvegarde auto'}
        </span>
      </div>}

      {/* Non-imported day: swap roster for an explicit empty state so
          the day doesn't read as "everyone absent". */}
      {!offDay && roster.data && notImported && !showAnyway && (
        <div className="sq-nodata" data-testid="sq-nodata">
          <span className="sq-nodata-ico"><CloudOff size={26} aria-hidden="true" /></span>
          <div className="sq-nodata-title">Aucune donnée pointeuse pour ce jour</div>
          <p className="sq-nodata-text">
            Le fichier de la pointeuse n'a pas encore été importé pour le {fmtFrLong(date)} —
            les employés apparaîtraient <b>absents à tort</b>. Importez le fichier, ou affichez
            quand même le tableau pour saisir des décisions manuelles (TT, congé…).
          </p>
          <div className="sq-nodata-actions">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => setImportOpen(true)}
              disabled={importDisabled}
              title={importDisabled ? importDisabledHint : undefined}
              data-testid="sq-nodata-import"
            >
              <Upload aria-hidden="true" /> Importer pointeuse
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => setShowAnyway(true)}
              data-testid="sq-nodata-show"
            >
              <Eye aria-hidden="true" /> Afficher le tableau quand même
            </button>
          </div>
        </div>
      )}

      {/* Attention banner */}
      {!offDay && entityId !== 'all' && rows.length > 0 && (!notImported || showAnyway) && (
        pendingRows.length > 0 && !dayReadOnly ? (
          <div className="sq-banner is-pending" data-testid="attention-banner">
            <span className="sq-banner-badge">!</span>
            <span className="sq-banner-text">
              <strong>{pendingRows.length} exception{pendingRows.length > 1 ? 's' : ''}</strong> à arbitrer avant validation
              {summary && ` — ${summary}`}.
            </span>
            <span className="sq-banner-spacer" />
            <button type="button" className="btn btn-sm btn-primary" onClick={() => launch(undefined)} data-testid="launch-arbitrage">
              <Zap aria-hidden="true" /> Lancer l'arbitrage
            </button>
          </div>
        ) : (
          <div className="sq-banner is-clear">
            <span className="sq-banner-badge"><Check size={14} aria-hidden="true" /></span>
            <span className="sq-banner-text">
              <strong>Toutes les exceptions sont traitées.</strong> La journée est prête à être validée.
            </span>
          </div>
        )
      )}

      {!offDay && roster.isLoading && <p className="page-placeholder">Chargement…</p>}
      {!offDay && roster.isError && (
        <p className="page-placeholder" data-testid="entry-error">
          Impossible de charger le pointage. Réessayez plus tard.
        </p>
      )}

      {!offDay && roster.data && (!notImported || showAnyway) && (
        <div className="card" style={{ padding: 0 }}>
          <DailyEntryTable
            date={date}
            rows={visibleRows}
            onLaunch={launch}
            readOnly={dayReadOnly}
            readOnlyHint={dayReadOnlyHint}
          />

          {/* Validation footer */}
          {entityId === 'all' ? (
            <div className="validation-bar">
              <div className="validation-text">Sélectionnez une entité pour valider la journée.</div>
            </div>
          ) : validated ? (
            <div className="validation-bar ready">
              <div className="validation-text">
                <span className="validation-text-icon"><Check size={11} aria-hidden="true" /></span>
                <span>
                  <strong>Journée validée</strong>
                  {validation.data?.validatedBy ? ` par ${validation.data.validatedBy}` : ''}
                </span>
              </div>
              {canValidateFinal && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={onReopen}
                  disabled={reopenDay.isPending}
                  data-testid="reopen-day"
                >
                  <RotateCcw aria-hidden="true" /> Rouvrir la journée
                </button>
              )}
              <button type="button" className="btn btn-sm btn-primary" disabled data-testid="validate-day">
                Validée
              </button>
            </div>
          ) : inReview ? (
            <div className="validation-bar review" data-testid="validation-bar-review">
              <div className="validation-text">
                <span className="validation-text-icon"><Eye size={11} aria-hidden="true" /></span>
                <span>
                  <strong>Saisie validée</strong>
                  {validation.data?.preparedBy ? ` par ${validation.data.preparedBy}` : ''} · en attente de
                  la validation finale
                </span>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={onValidateFinal}
                disabled={!canValidateFinal || validateFinal.isPending}
                title={canValidateFinal ? undefined : 'Réservé à la validation finale.'}
                data-testid="validate-day-final"
              >
                <Check aria-hidden="true" /> Validation finale
              </button>
            </div>
          ) : ready ? (
            <div className="validation-bar ready">
              <div className="validation-text">
                <span className="validation-text-icon"><Check size={11} aria-hidden="true" /></span>
                <span><strong>Tout est en ordre</strong> · prêt à valider</span>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={onValidate}
                disabled={validateDay.isPending}
                data-testid="validate-day"
              >
                <Check aria-hidden="true" /> Valider la journée
              </button>
            </div>
          ) : (
            <div className="validation-bar blocked">
              <div className="validation-text">
                <span className="validation-text-icon"><AlertTriangle size={11} aria-hidden="true" /></span>
                <span>
                  <strong>Validation impossible</strong> · {missingDecisions} décision
                  {missingDecisions > 1 ? 's' : ''} à prendre · {missingNotified} réponse
                  {missingNotified > 1 ? 's' : ''} « prévenu&nbsp;? » manquante{missingNotified > 1 ? 's' : ''}
                </span>
              </div>
              <button type="button" className="btn btn-sm btn-primary" disabled data-testid="validate-day">
                <Check aria-hidden="true" /> Valider la journée
              </button>
            </div>
          )}

          {/* Totals strip */}
          <div className="entry-totals">
            <span><strong className="mono">{rows.length}</strong> affichés</span>
            <span className="sep">·</span>
            <span><strong className="mono" style={{ color: 'var(--ok)' }}>{countTag(rows, 'ok')}</strong> présents pile</span>
            <span className="sep">·</span>
            <span><strong className="mono" style={{ color: 'var(--info)' }}>{countTag(rows, 'more')}</strong> &gt; attendu</span>
            <span className="sep">·</span>
            <span>
              <strong className="mono" style={{ color: 'var(--warn)' }}>{countTag(rows, 'late') + countTag(rows, 'less')}</strong> retards / courts
            </span>
            <span className="sep">·</span>
            <span><strong className="mono" style={{ color: 'var(--accent)' }}>{countTag(rows, 'tt')}</strong> TT</span>
            <span className="sep">·</span>
            <span><strong className="mono" style={{ color: 'var(--bad)' }}>{countTag(rows, 'abs')}</strong> absences</span>
          </div>
        </div>
      )}

      <ImportPointeuseModal open={importOpen} onClose={() => setImportOpen(false)} entityId={entityId} />
    </div>
  );
}
